import {
  getSelectAllCheckState,
  SelectAllCheckbox,
} from '@/components/select-all-checkbox';
import { useCohorts, useMyCohorts } from '@/hooks/queries/cohorts';
import { Cohort } from '@/types/cohort';
import {
  Chip,
  Select,
  SelectItem,
  Selection,
} from '@nextui-org/react';
import { useMemo } from 'react';

export const SELECT_ALL_COHORTS_KEY = '__select_all__';
export const COHORT_DIVIDER_KEY = '__cohort_divider__';

export const NON_COHORT_KEYS = new Set([
  SELECT_ALL_COHORTS_KEY,
  COHORT_DIVIDER_KEY,
]);

type CohortSelectOption =
  | {
      key: string;
      label: string;
      type: 'select-all';
      checkState: ReturnType<typeof getCohortSelectAllCheckState>;
    }
  | { key: string; label: string; type: 'divider' }
  | { key: string; label: string; type: 'cohort' };

export const getAllCohortKeys = (cohorts: Cohort[] = []): string[] =>
  cohorts.map((cohort) => cohort.id.toString());

export const areAllCohortsSelected = (
  selectedKeys: Selection,
  cohorts: Cohort[] = []
): boolean => {
  return getCohortSelectAllCheckState(selectedKeys, cohorts) === 'all';
};

export const getCohortSelectAllCheckState = (
  selectedKeys: Selection,
  cohorts: Cohort[] = []
) => {
  const targets = getAllCohortKeys(cohorts);

  if (targets.length === 0) {
    return getSelectAllCheckState(0, 0);
  }

  if (selectedKeys === 'all') {
    return getSelectAllCheckState(targets.length, targets.length);
  }

  const selectedSet = new Set(Array.from(selectedKeys, String));
  const selectedCount = targets.filter((key) => selectedSet.has(key)).length;

  return getSelectAllCheckState(selectedCount, targets.length);
};

export const getVisibleCohortSelectedKeys = (
  selectedKeys: Selection,
  cohorts: Cohort[] = []
): Selection => {
  const validKeys = new Set(getAllCohortKeys(cohorts));

  if (selectedKeys === 'all') {
    return new Set(getAllCohortKeys(cohorts));
  }

  return new Set(
    Array.from(selectedKeys, String).filter(
      (key) => validKeys.has(key) && !NON_COHORT_KEYS.has(key)
    )
  );
};

export const normalizeCohortSelectionChange = (
  keys: Selection,
  previousKeys: Selection,
  cohorts: Cohort[] = []
): Selection => {
  const cohortKeys = getAllCohortKeys(cohorts);

  if (keys === 'all') {
    return new Set(cohortKeys);
  }

  const keySet = new Set(Array.from(keys as Exclude<Selection, 'all'>, String));

  if (keySet.has(SELECT_ALL_COHORTS_KEY)) {
    const hadAll = areAllCohortsSelected(previousKeys, cohorts);
    return hadAll ? new Set<string>() : new Set(cohortKeys);
  }

  return new Set(Array.from(keySet).filter((key) => !NON_COHORT_KEYS.has(key)));
};

export const resolveCohortIds = (
  selection: Selection,
  cohorts: Cohort[] = []
): number[] => {
  if (selection === 'all') {
    return cohorts.map((cohort) => cohort.id);
  }

  return Array.from(selection)
    .filter((id) => !NON_COHORT_KEYS.has(String(id)))
    .map((id) => Number(id));
};

export const getAllCohortSelection = (cohorts: Cohort[]): Selection =>
  new Set(getAllCohortKeys(cohorts));

export const toggleCohortSelectAll = (
  selectedKeys: Selection,
  cohorts: Cohort[] = []
): Selection => {
  const hadAll = areAllCohortsSelected(selectedKeys, cohorts);
  return hadAll ? new Set<string>() : getAllCohortSelection(cohorts);
};

interface CohortMultiSelectProps {
  selectedKeys: Selection;
  onSelectionChange: (keys: Selection) => void;
  label?: string;
  placeholder?: string;
  className?: string;
  isRequired?: boolean;
  isInvalid?: boolean;
  errorMessage?: string;
  fetchMyCohorts?: boolean;
}

export default function CohortMultiSelect({
  selectedKeys,
  onSelectionChange,
  label = 'Cohorts',
  placeholder = 'Select cohorts',
  className,
  isRequired = false,
  isInvalid = false,
  errorMessage,
  fetchMyCohorts = false,
}: CohortMultiSelectProps) {
  const { data: allCohorts, isLoading: isLoadingAllCohorts } = useCohorts(
    undefined,
    { enabled: !fetchMyCohorts }
  );
  const { data: myCohorts, isLoading: isLoadingMyCohorts } = useMyCohorts({
    enabled: fetchMyCohorts,
  });

  const cohorts = fetchMyCohorts ? myCohorts : allCohorts;
  const isLoading = fetchMyCohorts ? isLoadingMyCohorts : isLoadingAllCohorts;
  const cohortResults = cohorts?.results ?? [];

  const allSelected = useMemo(
    () => areAllCohortsSelected(selectedKeys, cohortResults),
    [cohortResults, selectedKeys]
  );

  const selectAllCheckState = useMemo(
    () => getCohortSelectAllCheckState(selectedKeys, cohortResults),
    [cohortResults, selectedKeys]
  );

  const visibleSelectedKeys = useMemo(
    () => getVisibleCohortSelectedKeys(selectedKeys, cohortResults),
    [cohortResults, selectedKeys]
  );

  const items = useMemo<CohortSelectOption[]>(
    () => [
      {
        key: SELECT_ALL_COHORTS_KEY,
        label: allSelected ? 'All selected' : 'Select all',
        type: 'select-all',
        checkState: selectAllCheckState,
      },
      { key: COHORT_DIVIDER_KEY, label: '', type: 'divider' },
      ...cohortResults.map((cohort) => ({
        key: cohort.id.toString(),
        label: cohort.name,
        type: 'cohort' as const,
      })),
    ],
    [allSelected, cohortResults, selectAllCheckState]
  );

  const handleSelectAllPress = () => {
    onSelectionChange(toggleCohortSelectAll(selectedKeys, cohortResults));
  };

  const handleSelectionChange = (keys: Selection) => {
    if (keys !== 'all') {
      const keySet = new Set(
        Array.from(keys as Exclude<Selection, 'all'>, String)
      );

      if (keySet.has(SELECT_ALL_COHORTS_KEY)) {
        return;
      }
    }

    onSelectionChange(
      normalizeCohortSelectionChange(keys, selectedKeys, cohortResults)
    );
  };

  return (
    <Select
      label={label}
      placeholder={placeholder}
      selectionMode="multiple"
      selectedKeys={isLoading ? new Set<string>() : visibleSelectedKeys}
      onSelectionChange={handleSelectionChange}
      isLoading={isLoading}
      isMultiline
      isRequired={isRequired}
      isInvalid={isInvalid}
      errorMessage={errorMessage}
      items={items}
      className={className}
      classNames={{
        trigger: 'h-auto min-h-12 items-start py-2',
        value: 'h-auto whitespace-normal',
        innerWrapper: 'flex flex-wrap gap-1',
      }}
      renderValue={(selectedItems) => {
        if (allSelected) {
          return (
            <Chip size="sm" variant="flat">
              All selected
            </Chip>
          );
        }

        const selectedCohortItems = selectedItems.filter(
          (item) => !NON_COHORT_KEYS.has(String(item.key))
        );

        if (selectedCohortItems.length === 0) {
          return null;
        }

        return (
          <div className="flex flex-wrap gap-2">
            {selectedCohortItems.map((item) => (
              <Chip key={item.key} size="sm" variant="flat">
                {item.textValue}
              </Chip>
            ))}
          </div>
        );
      }}
    >
      {(item) => {
        if (item.type === 'divider') {
          return (
            <SelectItem
              key={item.key}
              textValue=""
              isDisabled
              classNames={{
                base: 'min-h-0 h-2 rounded-none px-0 py-0 opacity-100 data-[hover=true]:bg-transparent data-[disabled=true]:opacity-100 pointer-events-none',
                wrapper: 'hidden',
                selectedIcon: 'hidden',
              }}
            >
              <div className="h-px w-[calc(100%+16px)] -mx-2 bg-divider" />
            </SelectItem>
          );
        }

        if (item.type === 'select-all') {
          return (
            <SelectItem
              key={item.key}
              textValue={item.label}
              hideSelectedIcon
              onPress={handleSelectAllPress}
            >
              <span className="flex items-center gap-2">
                <SelectAllCheckbox state={item.checkState} />
                {item.label}
              </span>
            </SelectItem>
          );
        }

        return (
          <SelectItem key={item.key} textValue={item.label}>
            {item.label}
          </SelectItem>
        );
      }}
    </Select>
  );
}
